import { Icon } from '@buggy/shared'
import { FunctionComponent, memo } from 'react'
import styled from 'styled-components'
import { useQueryParam } from 'use-query-params'
import { KolThumbnail } from '@/components/common/kol-thumbnail'
import UnsafeNewTabLink from '@/components/public-page/footer/components/unsafe-new-tab-link'
import routes from '@/hooks/use-authorization/routes'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Post } from '@/types/schema/api/radar-search-post'
import { PlatformType } from '@/types/schema/report-schema'
import isNotNil from '@/utils/is-not-nil'

interface PostDetailProps {
  post?: Post
  className?: string
  from?: string
}

const PostDetail: FunctionComponent<PostDetailProps> = ({
  post,
  className,
  from,
}) => {
  const { formatNumber, formatMessage, formatDate } = useIntl()

  const [eventFrom] = useQueryParam<string>('from')

  if (!post) {
    return null
  }

  const {
    kol_uuid,
    kol_name,
    title,
    text,
    like_count,
    comment_count,
    share_count,
    view_count,
    play_count,
    platform_type,
    publish_time,
    is_disable_like,
  } = post

  return (
    <Wrapper className={className}>
      <KolInfo
        as={UnsafeNewTabLink}
        href={routes.kolId.stringifyUrl(
          {
            kolId: kol_uuid,
          },
          { from: from ?? eventFrom },
        )}
      >
        <KolThumbnail imageSize={48} kolUuid={kol_uuid} />
        <span>{kol_name}</span>
      </KolInfo>
      {isNotNil(publish_time) && (
        <PublishDate>{formatDate(publish_time)}</PublishDate>
      )}
      {isNotNil(title) && <Title>{title}</Title>}
      {isNotNil(text) && <Content>{text}</Content>}
      <DataList>
        {isNotNil(like_count) && (
          <Data>
            <Icon name='thumbs-up' />
            {is_disable_like
              ? formatMessage({ id: 'general:hidden' })
              : formatNumber(like_count, { isCompactDisplay: true })}
          </Data>
        )}
        {!!comment_count && (
          <Data>
            <Icon name='message-circle' />
            {formatNumber(comment_count, { isCompactDisplay: true })}
          </Data>
        )}
        {/** IG 不顯示觀看數，是歷史包袱，資料庫欄位太古老所以不能動 https://www.notion.so/ikala/Staging-Search-IG-5e3d1f568609498ea318a45ef9658244?pvs=4 */}
        {!!view_count && platform_type !== PlatformType.Instagram && (
          <Data>
            <Icon name='eye' />
            {formatNumber(view_count, { isCompactDisplay: true })}
          </Data>
        )}
        {!!play_count && (
          <Data>
            <Icon name='play' />
            {formatNumber(play_count, { isCompactDisplay: true })}
          </Data>
        )}
        {!!share_count && (
          <Data>
            <Icon name='arrow-forward-outlined' />
            {formatNumber(share_count, { isCompactDisplay: true })}
          </Data>
        )}
      </DataList>
    </Wrapper>
  )
}

const KolInfo = styled.a`
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 18px;
  font-weight: 700;
  pointer-events: none;

  &,
  &:hover {
    color: ${({ theme }): string => theme.colors.text.primary};
  }

  * {
    pointer-events: all;
  }
`

const PublishDate = styled.div`
  font-size: 12px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
`

const Title = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }): string => theme.colors.text.primary};
`

const Content = styled.div`
  font-size: 14px;
  color: ${({ theme }): string => theme.colors.text.secondary};
`

const Data = styled.div`
  font-size: 12px;
  color: ${({ theme }): string => theme.colors.text.tooltip};
  fill: ${({ theme }): string =>
    theme.colors.text.tooltip}; /* shared/icon 有些圖案不吃 color */
  display: flex;
  align-items: center;

  > span {
    font-size: 14px;
    margin-right: 3px;
  }
`

const DataList = styled.div`
  display: flex;
  flex-wrap: wrap;

  ${Data} {
    margin-right: 10px;
  }
`

const Wrapper = styled.div`
  padding: 16px 24px 0;
  padding-right: 16px;
  overflow: auto;

  ${KolInfo} {
    margin-bottom: 16px;
    margin-right: 28px; /* 防止撞到 x 按鈕 */
  }
  ${PublishDate} {
    margin-bottom: 8px;
  }
  ${Title} {
    margin-bottom: 4px;
  }
  ${Content} {
    margin-bottom: 8px;
  }
`

export default styled(memo(PostDetail))``
